import React, { useState } from 'react';
import { useSelector } from "react-redux";
import {
  Box, Collapse, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Tooltip, Button,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  TextField, FormControl, InputLabel, Select, MenuItem, Checkbox
} from '@mui/material';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { deepCopy } from "../../components/Common";
import { createTheme, ThemeProvider } from '@mui/material/styles';

const availableExtras = ["Per 90mm PVC downpipe", "Per bolt to slab", "Zincalume roof"];
const designTemplateTypes = ["National Carport"];
const linkableProductCodes = ["LCDISC"];

const headerStyles = {
  fontFamily: "'canada-type-gibson', sans-serif",
  fontSize: '13px',
  fontWeight: '400'
};

const contentStyles = {
  fontFamily: "'canada-type-gibson', sans-serif",
  fontSize: '13px',
  fontWeight: '200'
};

const theme = createTheme({
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '6px',
        }
      }
    }
  }
});

const CustomSelect = ({ label, name, value, onChange, options, disabled }) => (
  <FormControl fullWidth margin="dense" variant="outlined" sx={{ marginBottom: 2 }}>
    <InputLabel id={`${name}-label`}>{label}</InputLabel>
    <Select
      labelId={`${name}-label`}
      name={name}
      value={value}
      onChange={onChange}
      label={label}
      disabled={disabled}
    >
      {options.map(option => (
        <MenuItem key={option} value={option}>{option}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

const AddRowDialog = ({ open, onClose, onSave }) => {
  const config = useSelector((state) => state.config);
  const [newRow, setNewRow] = useState({
    DesignTemplateType: '',
    DesignTemplateName: '',
    BasePrice: '',
    LinkedProductCode: '',
    Extras: []
  });

  const designTemplateNames = {
    "National Carport": Object.getOwnPropertyNames(
      config?.design_options["National Carport"]?.design_templates || {}
    )
  };

  const handleChange = event => {
    const { name, value } = event.target;
    setNewRow(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    onSave({ ...newRow, id: Date.now() });
    onClose();
    setNewRow({
      DesignTemplateType: '',
      DesignTemplateName: '',
      BasePrice: '',
      LinkedProductCode: '',
      Extras: []
    });
  };

  const isFormComplete = () => newRow.DesignTemplateType && newRow.DesignTemplateName && newRow.BasePrice > 0 && newRow.LinkedProductCode;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Add New Row</DialogTitle>
      <DialogContent>
        <CustomSelect
          label="Design Template Type"
          name="DesignTemplateType"
          value={newRow.DesignTemplateType}
          onChange={handleChange}
          options={designTemplateTypes}
          disabled={false}
        />
        <CustomSelect
          label="Design Template Name"
          name="DesignTemplateName"
          value={newRow.DesignTemplateName}
          onChange={handleChange}
          options={newRow.DesignTemplateType ? designTemplateNames[newRow.DesignTemplateType] : []}
          disabled={!newRow.DesignTemplateType}
        />
        <TextField
          fullWidth
          margin="dense"
          name="BasePrice"
          label="Base Price"
          type="number"
          variant="outlined"
          value={newRow.BasePrice}
          onChange={handleChange}
          inputProps={{ min: "0.01", step: "0.01" }}
        />
        <CustomSelect
          label="Link To"
          name="LinkedProductCode"
          value={newRow.LinkedProductCode}
          onChange={handleChange}
          options={linkableProductCodes}
          disabled={false}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={!isFormComplete()}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

const EditDialog = ({ open, onClose, row, onSave }) => {
  const config = useSelector((state) => state.config);
  const [formData, setFormData] = useState(row);

  const designTemplateNames = {
    "National Carport": Object.getOwnPropertyNames(
      config?.design_options["National Carport"]?.design_templates || {}
    )
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    onSave(formData);
    onClose();
  };

  const isFormComplete = () => formData.DesignTemplateType && formData.DesignTemplateName && formData.BasePrice > 0 && formData.LinkedProductCode;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Edit Base</DialogTitle>
      <DialogContent>
        <CustomSelect
          label="Design Template Type"
          name="DesignTemplateType"
          value={formData.DesignTemplateType}
          onChange={handleChange}
          options={designTemplateTypes}
          disabled={false}
        />
        <CustomSelect
          label="Design Template Name"
          name="DesignTemplateName"
          value={formData.DesignTemplateName}
          onChange={handleChange}
          options={formData.DesignTemplateType ? designTemplateNames[formData.DesignTemplateType] : []}
          disabled={!formData.DesignTemplateType}
        />
        <TextField
          fullWidth
          margin="dense"
          name="BasePrice"
          label="Base Price"
          type="number"
          variant="outlined"
          value={formData.BasePrice}
          onChange={handleChange}
          inputProps={{ min: "0.01", step: "0.01" }}
        />
        <CustomSelect
          label="Link Product"
          name="LinkedProductCode"
          value={formData.LinkedProductCode}
          onChange={handleChange}
          options={linkableProductCodes}
          disabled={false}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!isFormComplete()}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

const AddExtraDialog = ({ open, onClose, onSave }) => {
  const [newExtra, setNewExtra] = useState({
    Description: '',
    Price: ''
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setNewExtra((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    onSave(newExtra);
    onClose();
  };

  const isFormComplete = () => newExtra.Description && newExtra.Price;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Add New Extra</DialogTitle>
      <DialogContent>
        <CustomSelect
          label="Description"
          name="Description"
          value={newExtra.Description}
          onChange={handleChange}
          options={availableExtras}
          disabled={false}
        />
        <TextField
          fullWidth
          margin="dense"
          name="Price"
          label="Price"
          type="number"
          variant="outlined"
          value={newExtra.Price}
          onChange={handleChange}
          inputProps={{ step: "0.01" }}
          sx={{ marginTop: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={!isFormComplete()}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

const EditExtraDialog = ({ open, onClose, row, extraId, onSave }) => {
  const [formData, setFormData] = useState(row.Extras.find((extra) => extra.id === extraId));

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    onSave(formData);
    onClose();
  };

  const isFormComplete = () => formData.Description && formData.Price;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Edit Extra</DialogTitle>
      <DialogContent>
        <CustomSelect
          label="Description"
          name="Description"
          value={formData.Description}
          onChange={handleChange}
          options={availableExtras}
          disabled={false}
        />
        <TextField
          fullWidth
          margin="dense"
          name="Price"
          label="Price"
          type="number"
          variant="outlined"
          value={formData.Price}
          onChange={handleChange}
          inputProps={{ step: "0.01" }}
          sx={{ marginTop: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!isFormComplete()}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

const Row = ({ row, onUpdate, onDelete, onAddExtra }) => {
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [addExtraOpen, setAddExtraOpen] = useState(false);
  const [editingExtra, setEditingExtra] = useState(false);
  const [targetExtraId, setTargetExtraId] = useState(null);

  const handleDelete = () => {
    onDelete(row.id);
  };

  const handleUpdate = (newData) => {
    onUpdate(newData);
  };

  const handleAddExtra = (newExtra) => {
    onAddExtra(row.id, { ...newExtra, id: Date.now() });
    setAddExtraOpen(false);
  };

  const handleEditExtra = (target) => {
    setTargetExtraId(target);
    setEditingExtra(true);
  };

  const saveEditedExtra = (updatedExtra) => {
    const updatedExtras = row.Extras.map((extra) =>
      extra.id === updatedExtra.id ? updatedExtra : extra
    );
    onUpdate({ ...row, Extras: updatedExtras });
    setEditingExtra(false);
  };

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={contentStyles} component="th" scope="row">
          {row.DesignTemplateType}
        </TableCell>
        <TableCell sx={contentStyles}>{row.DesignTemplateName}</TableCell>
        <TableCell sx={contentStyles}>{row.LinkedProductCode}</TableCell>
        <TableCell sx={contentStyles} align="right">{row.BasePrice}</TableCell>
        <TableCell align="right">
          <IconButton onClick={() => setEditOpen(true)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={handleDelete}>
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Button
                sx={headerStyles}
                startIcon={<AddIcon />}
                onClick={() => setAddExtraOpen(true)}
              >
                Add Extra Price
              </Button>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell sx={headerStyles}>Description</TableCell>
                    <TableCell sx={headerStyles} align="right">Price ($)</TableCell>
                    <TableCell sx={headerStyles} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.Extras.map((extra) => (
                    <TableRow key={extra.id}>
                      <TableCell />
                      <TableCell sx={contentStyles}>{extra.Description}</TableCell>
                      <TableCell sx={contentStyles} align="right">{extra.Price}</TableCell>
                      <TableCell sx={contentStyles} align="right">
                        <IconButton onClick={() => handleEditExtra(extra.id)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            const updatedExtras = row.Extras.filter(
                              (e) => e.id !== extra.id
                            );
                            onUpdate({ ...row, Extras: updatedExtras });
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      {editOpen && (
        <EditDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          row={row}
          onSave={handleUpdate}
        />
      )}
      {addExtraOpen && (
        <AddExtraDialog
          open={addExtraOpen}
          onClose={() => setAddExtraOpen(false)}
          onSave={handleAddExtra}
        />
      )}
      {editingExtra && (
        <EditExtraDialog
          open={Boolean(editingExtra)}
          onClose={() => setEditingExtra(false)}
          row={row}
          extraId={targetExtraId}
          onSave={saveEditedExtra}
        />
      )}
    </React.Fragment>
  );
};

const FixedPriceKits = (props) => {
  const businessDetails = deepCopy(props.business);
  const [rows, setRows] = useState(businessDetails?.FixedPriceKits || []);
  const [addRowOpen, setAddRowOpen] = useState(false);

  const handleAddRow = (newRow) => {
    setRows((prevRows) => {
      const updatedRows = [...prevRows, newRow];
      businessDetails.FixedPriceKits = updatedRows;
      props.updateBusiness(businessDetails);
      return updatedRows;
    });
  };

  const handleRowUpdate = (newData) => {
    setRows((prevRows) => {
      const updatedRows = prevRows.map((row) => (row.id === newData.id ? newData : row));
      businessDetails.FixedPriceKits = updatedRows;
      props.updateBusiness(businessDetails);
      return updatedRows;
    });
  };

  const handleRowDelete = (id) => {
    setRows((prevRows) => {
      const updatedRows = prevRows.filter((row) => row.id !== id);
      businessDetails.FixedPriceKits = updatedRows;
      props.updateBusiness(businessDetails);
      return updatedRows;
    });
  };

  const handleAddExtra = (rowId, newExtra) => {
    setRows((prevRows) => {
      const updatedRows = prevRows.map((row) =>
        row.id === rowId
          ? { ...row, Extras: [...row.Extras, { ...newExtra, id: Math.random() }] }
          : row
      );
      businessDetails.FixedPriceKits = updatedRows;
      props.updateBusiness(businessDetails);
      return updatedRows;
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: '100%' }}>
        <Button sx={headerStyles} onClick={() => setAddRowOpen(true)} startIcon={<AddIcon />}>
          Add Base Kit Price
        </Button>
        <TableContainer component={Paper}>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell sx={headerStyles}>Design Type</TableCell>
                <TableCell sx={headerStyles}>Design Name</TableCell>
                <Tooltip title="Price adjustment is applied to this product" placement="top" arrow>
                  <TableCell sx={{ fontFamily: "'canada-type-gibson', sans-serif", fontSize: '13px', fontWeight: '400' }}>
                    Linked To
                  </TableCell>
                </Tooltip>
                <TableCell sx={headerStyles} align="right">Price ($)</TableCell>
                <TableCell sx={headerStyles} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <Row
                  key={row.id}
                  row={row}
                  onUpdate={handleRowUpdate}
                  onDelete={handleRowDelete}
                  onAddExtra={handleAddExtra}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {addRowOpen && (
          <AddRowDialog
            open={addRowOpen}
            onClose={() => setAddRowOpen(false)}
            onSave={handleAddRow}
          />
        )}
      </Box>
    </ThemeProvider>
  );
};

export default FixedPriceKits;